import { Lock, Pencil } from "lucide-react";
import {
  useDataProvider,
  useGetIdentity,
  useGetList,
  useNotify,
  useShowContext,
  useTranslate,
} from "ra-core";
import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Show } from "@/components/admin/show";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import { ListCardsSkeleton } from "../design/ListStates";
import { formatDateOnly } from "../incentives/incentiveProgramForm";
import { formatMxn } from "../quoter/PlanSchedule";
import type {
  AppRole,
  PropertyOption,
  PropertyOptionOwner,
  PropertyOptionStatus,
} from "../types";
import { usePropertyTypes } from "./InventoryFilters";
import {
  OperationLabel,
  OpcionStatusPill,
  OriginPill,
  ResponsableLabel,
} from "./OpcionBadges";
import {
  canEditOption,
  canSeeOwner,
  canSellOption,
  getStatusActions,
  toOpcionErrorMessage,
} from "./opcionRules";
import { PromotionCard } from "./PromotionCard";
import { SellOptionDialog } from "./SellOptionDialog";

const EMPTY = "—";

/** Physical specifications shown on the detail (never the listing date). */
const SPEC_KEYS = [
  "levels",
  "bedrooms",
  "bathrooms",
  "area_m2",
  "parking",
] as const;

/**
 * Almikia Epic EC (TASK-030): option detail. Status changes are explicit
 * actions ("Retirar" instead of any delete, which does not exist). The
 * owner's private contact is requested and rendered only for direccion,
 * lead_manager and the responsable; everyone else gets a plain privacy note.
 */
export const OpcionShow = () => (
  <Show actions={false}>
    <OpcionShowLayout />
  </Show>
);

const Row = ({ label, children }: { label: string; children: ReactNode }) => {
  const translate = useTranslate();
  return (
    <div>
      <dt className="text-sm text-muted-foreground">{translate(label)}</dt>
      <dd className="text-base">{children}</dd>
    </div>
  );
};

const OpcionShowLayout = () => {
  const { record, isPending, refetch } = useShowContext<PropertyOption>();
  const { identity } = useGetIdentity();
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const [isChanging, setIsChanging] = useState(false);
  const { labels: typeLabels } = usePropertyTypes();

  if (isPending || !record || !identity) return <ListCardsSkeleton rows={3} />;

  const role = identity.role as AppRole;
  const userId = identity.id;
  const canEdit = canEditOption(role, userId, record);
  const statusActions = getStatusActions(role, userId, record);
  const showOwner = canSeeOwner(role, userId, record);
  const canSell = canSellOption(role, userId, record);

  const changeStatus = async (status: PropertyOptionStatus) => {
    setIsChanging(true);
    try {
      await dataProvider.update("property_options", {
        id: record.id,
        data: { status },
        previousData: record,
      });
      notify("crm.opciones.status_changed_notice");
      refetch();
    } catch (error) {
      notify(toOpcionErrorMessage(error), { type: "error" });
    } finally {
      setIsChanging(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-xl font-semibold">{record.address}</h2>
            <OpcionStatusPill status={record.status} />
            <OriginPill origin={record.origin ?? "opcion"} />
          </div>
          <dl className="grid gap-3 sm:grid-cols-2">
            <Row label="resources.property_options.fields.zone">
              {record.zone || EMPTY}
            </Row>
            <Row label="resources.property_options.fields.property_type">
              {typeLabels.get(record.property_type) ??
                translate(`crm.opciones.type.${record.property_type}`, {
                  _: record.property_type,
                })}
            </Row>
            <Row label="resources.property_options.fields.operation">
              <OperationLabel
                operation={record.operation ?? "venta"}
                furnished={record.furnished ?? null}
              />
            </Row>
            {record.origin === "exclusiva" ? (
              <>
                <Row label="resources.property_options.fields.exclusivity_signed_on">
                  <span className="tabular-nums">
                    {record.exclusivity_signed_on
                      ? formatDateOnly(record.exclusivity_signed_on)
                      : EMPTY}
                  </span>
                </Row>
                <Row label="resources.property_options.fields.exclusivity_ends_on">
                  <span className="tabular-nums">
                    {record.exclusivity_ends_on
                      ? formatDateOnly(record.exclusivity_ends_on)
                      : EMPTY}
                  </span>
                </Row>
              </>
            ) : null}
            {SPEC_KEYS.map((key) => (
              <Row key={key} label={`resources.property_options.fields.${key}`}>
                <span className="tabular-nums">{record[key] ?? EMPTY}</span>
              </Row>
            ))}
            <Row label="resources.property_options.fields.price">
              <span className="tabular-nums">{formatMxn(record.price)}</span>
            </Row>
            <Row label="resources.property_options.fields.responsable">
              <ResponsableLabel option={record} />
            </Row>
          </dl>
          {canEdit || canSell || statusActions.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {canSell ? (
                <SellOptionDialog option={record} role={role} userId={userId} />
              ) : null}
              {canEdit ? (
                <Button asChild variant="outline" className="min-h-11">
                  <Link to={`/property_options/${record.id}`}>
                    <Pencil className="size-4" aria-hidden="true" />
                    {translate("crm.opciones.edit_action")}
                  </Link>
                </Button>
              ) : null}
              {statusActions.map((status) => (
                <Button
                  key={status}
                  type="button"
                  variant="outline"
                  className="min-h-11"
                  disabled={isChanging}
                  onClick={() => changeStatus(status)}
                >
                  {translate(`crm.opciones.actions.${status}`)}
                </Button>
              ))}
            </div>
          ) : null}
        </CardContent>
      </Card>

      <PromotionCard key={record.id} option={record} canEdit={canEdit} />

      {showOwner ? (
        <OwnerCard optionId={record.id} />
      ) : (
        <Card>
          <CardContent
            data-testid="owner-private-note"
            className="flex items-center gap-2 text-sm text-muted-foreground"
          >
            <Lock className="size-4 shrink-0" aria-hidden="true" />
            {translate("crm.opciones.owner_private")}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

/** Requested only when the viewer may see the owner (the parent decides). */
const OwnerCard = ({ optionId }: { optionId: PropertyOption["id"] }) => {
  const translate = useTranslate();
  const { data, isPending } = useGetList<PropertyOptionOwner>(
    "property_option_owners",
    {
      filter: { option_id: optionId },
      pagination: { page: 1, perPage: 1 },
      sort: { field: "id", order: "ASC" },
    },
  );
  const owner = data?.[0];
  return (
    <Card>
      <CardContent className="space-y-3" data-testid="owner-contact">
        <h3 className="font-semibold">
          {translate("crm.opciones.owner_title")}
        </h3>
        {isPending ? (
          <ListCardsSkeleton rows={1} />
        ) : owner ? (
          <dl className="grid gap-3 sm:grid-cols-2">
            <Row label="resources.property_option_owners.fields.full_name">
              {owner.full_name}
            </Row>
            <Row label="resources.property_option_owners.fields.phone">
              <span className="tabular-nums">{owner.phone || EMPTY}</span>
            </Row>
            <Row label="resources.property_option_owners.fields.email">
              {owner.email || EMPTY}
            </Row>
            <Row label="resources.property_option_owners.fields.notes">
              {owner.notes || EMPTY}
            </Row>
          </dl>
        ) : (
          <p className="text-sm text-muted-foreground">
            {translate("crm.opciones.owner_empty")}
          </p>
        )}
      </CardContent>
    </Card>
  );
};
